import React, { useCallback } from "react";

import { css } from "@compiled/react";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/libraries/design/components";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { ColumnLayout } from "@/libraries/design/foundations";
import { TabDataType, TabsSection } from "@/libraries/design/patterns/tabs/src";
import { TabsSection as WacRefreshTabsSection } from "@/libraries/design/patterns/tabs/wac-refresh-tabs";
import {
    EnrollmentType,
    useExperiment
} from "@/utils/experiment/use-experiment";
import { wacRefreshQ4Fy26Experiment } from "@/utils/experiment/wacRefreshExperiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

export interface UniformTabsProps {
    headerText?: string;
    subHeaderText?: string;
    enableRovoBorder?: boolean;
    enableResponsiveTabSize?: boolean;
    bgColor?: BgColors;
    tabSize?: "large" | "small";
}

const uniformTabsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.500"),
    width: "100%",
    overflow: "hidden",
    [media.below.md]: {
        "gridTemplateColumns": "unset",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "unset"
        }
    }
});

const UniformTabs12Col: React.FC<UniformTabsProps> = ({
    headerText,
    subHeaderText,
    bgColor = "bgWhite",
    enableRovoBorder = false,
    enableResponsiveTabSize = false,
    tabSize = "large"
}) => {
    const isWACRefreshGateEnabled = checkFeatureFlagGate(
        featureFlags.wacRefreshTeam26Gate
    );
    const checkWacRefreshExperiment = useExperiment(wacRefreshQ4Fy26Experiment);
    const isWacRefresh =
        isWACRefreshGateEnabled ||
        (checkWacRefreshExperiment.type === EnrollmentType.ENROLLED &&
            checkWacRefreshExperiment.value);

    const renderTabs: React.ComponentType<UniformSlotWrapperComponentProps> =
        useCallback(
            ({ items }) => {
                const tabsData = items.map((item, index) => {
                    const params =
                        // @ts-ignore
                        item.props?.children?.props?.data?.parameters;

                    return {
                        id: params?.id?.value ?? `tab-${index}`,
                        title: params?.title?.value,
                        iconUrl: params?.iconUrl?.value,
                        tabComponent: item
                    } as TabDataType;
                });

                if (isWacRefresh) {
                    return (
                        <WacRefreshTabsSection
                            tabsData={tabsData}
                            tabSize={tabSize}
                        />
                    );
                }

                return (
                    <TabsSection
                        alignment="center"
                        tabsData={tabsData}
                        tabSize={tabSize}
                        bgColor={bgColor}
                        enableRovoBorder={enableRovoBorder}
                        enableResponsiveTabSize={enableResponsiveTabSize}
                    />
                );
            },
            [bgColor, enableRovoBorder, enableResponsiveTabSize]
        );

    return (
        <div css={uniformTabsContainerStyles}>
            {(headerText || subHeaderText) && (
                <ColumnLayout layout="8">
                    <ContentBlock
                        title={headerText}
                        text={subHeaderText}
                        titleAs="h2"
                        alignment="center"
                    />
                </ColumnLayout>
            )}
            <UniformSlot name="tabs" wrapperComponent={renderTabs} />
        </div>
    );
};

export default UniformTabs12Col;
